if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      const detachedCustomizerModal = document.body.querySelector(':scope > [data-hc-customizer-modal]');
      if (detachedCustomizerModal) detachedCustomizerModal.remove();
      document.body.querySelectorAll(':scope > .amzcustom-modal').forEach((modal) => modal.remove());
      document.body.classList.remove('amzcustom-locked');

      delete this.modalContent.dataset.amazonCustomizerMounted;
      delete this.modalContent.dataset.customizerReady;
      delete this.modalContent.dataset.hcFormInitialized;
      delete this.modalContent.dataset.hcGalleryReady;
      delete this.modalContent.dataset.section;
      delete this.modalContent.hcRefreshGallery;
      this.modalContent.innerHTML = '';
      this.modalContent.classList.remove('hc-product-main');

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then(async (responseText) => {
          const styles = window.__preaQuickAddLoaderConfig || {};
          const styleList = [
            { href: styles.quickAddCss, id: 'prea-quick-add-css' },
            { href: styles.quickAddProductModalCss, id: 'prea-quick-add-product-modal-css' },
            { href: styles.sectionMainProductCustomCss, id: 'prea-section-main-product-custom-css' },
            { href: styles.componentUrgencyDeliveryCss, id: 'prea-component-urgency-delivery-css' },
            { href: styles.componentLocalizationFormCss, id: 'prea-component-localization-form-css' },
            { href: styles.componentProductLabelsCss, id: 'prea-component-product-labels-css' },
            { href: styles.preaVendorOverridesCss, id: 'prea-vendor-overrides-css' }
          ].filter((item) => Boolean(item.href));

          if (window.__preaLoadStyles) {
            window.__preaLoadStyles(styleList);
          }
          await Promise.all(styleList.map((item) => {
            const link = document.getElementById(item.id);
            return !link || link.sheet ? Promise.resolve() : new Promise((resolve) => {
              link.addEventListener('load', resolve, { once: true });
              link.addEventListener('error', resolve, { once: true });
            });
          }));
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"], section[id^="HcProductMain-"]');
          if (!this.productElement) return;
          if (this.productElement.classList.contains('hc-product-main') && !window.EmblaCarousel) {
            await window.__preaLoadScript(window.__preaThemeAssets.emblaCarousel, 'prea-embla-carousel');
          }
          if (this.productElement.classList.contains('hc-product-main') && !window.__preaExternalCustomizerOverlay) {
            await window.__preaLoadScript(window.__preaThemeAssets.externalCustomizerOverlay, 'prea-external-customizer-overlay');
          }
          if (this.productElement.classList.contains('hc-product-main') && !window.HcProductForm?.init && window.__preaThemeAssets?.hcProductForm) {
            await window.__preaLoadScript(window.__preaThemeAssets.hcProductForm, 'prea-hc-product-form');
          }
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          // Preserve the custom product scope used by labels, sizing and app controls.
          this.modalContent.classList.toggle('hc-product-main', this.productElement.classList.contains('hc-product-main'));
          this.modalContent.dataset.section = this.productElement.dataset.section;
          delete this.modalContent.dataset.amazonCustomizerMounted;
          delete this.modalContent.dataset.customizerReady;
          delete this.modalContent.dataset.hcFormInitialized;
          delete this.modalContent.dataset.hcGalleryReady;
          await this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
          this.resetScrollPosition();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();

          const customizerSource = this.modalContent.querySelector('[data-hc-customizer-app-source]');
          const customizerTarget = this.modalContent.querySelector('[data-hc-customizer-app-target]');
          if (customizerSource && customizerTarget) {
            customizerSource.hidden = false;
            customizerTarget.replaceChildren(customizerSource);
            this.modalContent.dataset.amazonCustomizerMounted = 'true';
          }

          window.HcDeliverySummary?.init?.(this.modalContent);
          window.HcProductGallery?.init?.(this.modalContent);
          window.HcProductForm?.init?.(this.modalContent);
          super.show(opener);
          this.modalContent.hcRefreshGallery?.();
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    async setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      for (const oldScriptTag of element.querySelectorAll('script')) {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        const loaded = newScriptTag.src ? new Promise((resolve) => {
          newScriptTag.async = false;
          newScriptTag.addEventListener('load', resolve, { once: true });
          newScriptTag.addEventListener('error', resolve, { once: true });
        }) : Promise.resolve();
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        await loaded;
      }
    }

    resetScrollPosition() {
      this.modalContent.scrollTop = 0;

      this.modalContent
        .querySelectorAll('.hc-product__info-wrapper, .hc-product__main-image, .hc-product__thumbnails')
        .forEach((element) => {
          element.scrollTop = 0;
          element.scrollLeft = 0;
        });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      if (!product) return;
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
